import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Copy, Share2, RefreshCw, Loader2, Link2, ExternalLink, Check, X, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MyBookingLinkCard
 *
 * Shown on the talent dashboard. Displays the talent's personal /book/:slug
 * URL with copy + native share support, plus a lightweight stats strip
 * (total / pending / accepted) pulled from /talent/booking-link.
 *
 * Also lets talent customize their slug (e.g. `/book/carah-stephens`).
 * Availability is checked live on each keystroke (debounced) via
 * `GET /talent/booking-link/check?slug=…`. Old slugs are retired into
 * `talent_slug_aliases` server-side so printed collateral / archived IG
 * bios keep routing to the new URL.
 */
const MyBookingLinkCard = ({ defaultOpen = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  // Collapsible — collapsed by default to keep the mobile home screen tidy.
  const [open, setOpen] = useState(defaultOpen);

  // Customize dialog
  const [editOpen, setEditOpen] = useState(false);
  const [candidate, setCandidate] = useState('');
  const [checkState, setCheckState] = useState({ checking: false, available: null, reason: null, normalized: null });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/talent/booking-link');
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live availability probe — kept above the early returns below so hook
  // ordering stays stable across renders (React rules of hooks).
  const runAvailability = useCallback(async (value) => {
    if (!value || value.trim().length < 3) {
      setCheckState({ checking: false, available: null, reason: 'Too short — use at least 3 characters.', normalized: null });
      return;
    }
    setCheckState((s) => ({ ...s, checking: true }));
    try {
      const r = await api.get('/talent/booking-link/check', { params: { slug: value } });
      setCheckState({
        checking: false,
        available: r.data.available,
        reason: r.data.reason,
        normalized: r.data.slug,
      });
    } catch (e) {
      setCheckState({
        checking: false,
        available: false,
        reason: e?.response?.data?.detail || 'Invalid username',
        normalized: null,
      });
    }
  }, []);

  if (loading) {
    return (
      <Card><CardContent className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></CardContent></Card>
    );
  }
  if (!data) return null;

  const origin = window.location.origin;
  // Displayed + "Preview" use the clean, professional `/book/:slug` URL.
  // Copy + native Share use the `/api/l/:slug` resolver, which serves a
  // personalised OG preview (photo, name, title) to social crawlers that
  // don't run JS, then instant-redirects real humans to `/book/:slug`.
  const url = `${origin}/book/${data.slug}`;
  const humanUrl = url;
  const shareUrl = `${origin}/api/l/${data.slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Preview-ready link copied — paste it anywhere, it renders a real preview card.');
    } catch {
      toast.error('Copy failed');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Book me on iEvntür',
          text: 'Use my personal link to hire me directly for your event:',
          url: shareUrl,
        });
      } catch {}
    } else {
      copy();
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerating your link will break your old one. Continue?')) return;
    setRegenerating(true);
    try {
      const r = await api.post('/talent/booking-link/regenerate');
      setData((d) => ({ ...d, slug: r.data.slug }));
      toast.success('New link ready');
    } catch (e) {
      toast.error('Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  // --- Customize dialog ---------------------------------------------------

  const openEdit = () => {
    setCandidate(data.slug || '');
    setCheckState({ checking: false, available: null, reason: null, normalized: null });
    // Defer to next frame so any previously-closing Radix overlay (e.g. the
    // What's-new modal) fully unmounts before we mount the Customize dialog.
    // Without this, the first click can land while the outgoing overlay is
    // still absorbing pointer events and appear to no-op.
    requestAnimationFrame(() => setEditOpen(true));
  };

  const onCandidateChange = (value) => {
    setCandidate(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 350ms debounce — long enough to avoid spamming the API on fast typing,
    // short enough that the "available ✓" badge feels responsive.
    debounceRef.current = setTimeout(() => runAvailability(value), 350);
  };

  const saveCustomSlug = async () => {
    if (!checkState.available) {
      toast.error(checkState.reason || 'Pick an available username first');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put('/talent/booking-link', { slug: candidate });
      setData((d) => ({ ...d, slug: r.data.slug }));
      toast.success(r.data.changed ? `Username saved — now /book/${r.data.slug}` : 'No change needed');
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save username');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-cyan-50 to-violet-50 border-cyan-200" data-testid="my-booking-link-card">
      <CardHeader
        className="pb-2 cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
        role="button"
        data-testid="booking-link-toggle"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-cyan-600" />
              My Booking Link
            </CardTitle>
            {!open && (
              <p className="text-xs text-gray-600 mt-1" data-testid="booking-link-subtitle">
                Share your personal iEvntür booking page with clients.
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 text-cyan-700 shrink-0">
            <span className="text-xs font-semibold hidden sm:inline">{open ? 'Hide' : 'View Link'}</span>
            {open
              ? <ChevronUp className="w-5 h-5" data-testid="booking-link-chevron-up" />
              : <ChevronDown className="w-5 h-5" data-testid="booking-link-chevron-down" />}
          </div>
        </div>
      </CardHeader>
      {open && (
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600">
          Share this link on your IG bio, TikTok, email signature, or anywhere clients find you.
          iEvntür handles payments and platform fees automatically.
        </p>

        <div className="bg-white border border-gray-200 rounded-lg p-2 flex items-center gap-2" data-testid="booking-link-url">
          <code className="flex-1 text-xs text-gray-800 truncate">{url}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid="copy-link-btn">
            <Copy className="w-3 h-3 mr-1" /> Copy
          </Button>
        </div>
        <p className="text-[11px] text-cyan-700/80 -mt-1">
          Share this clean link anywhere — Instagram, TikTok, iMessage, email, or LinkedIn.
          It renders your photo, name and rating as a preview card, then takes clients to your booking page.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={share} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="share-link-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <a
            href={humanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-cyan-700 hover:text-cyan-800 px-3 py-1.5"
            data-testid="preview-link-btn"
          >
            <ExternalLink className="w-4 h-4" /> Preview
          </a>
          <Button
            size="sm"
            variant="outline"
            onClick={openEdit}
            className="ml-auto"
            data-testid="customize-slug-btn"
          >
            <Pencil className="w-3 h-3 mr-1" />
            <span className="text-xs">Customize</span>
          </Button>
          <Button size="sm" variant="outline" onClick={regenerate} disabled={regenerating} data-testid="regenerate-link-btn">
            {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span className="ml-1 text-xs">Regenerate</span>
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-cyan-200">
          <div className="text-center">
            <div className="text-xl font-semibold text-gray-900">{data.total_requests}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Total</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-amber-600">{data.pending_requests}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold text-emerald-600">{data.accepted_requests}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Accepted</div>
          </div>
        </div>
      </CardContent>
      )}

      {/* Customize dialog — live availability check + uniqueness gating */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="customize-slug-dialog">
          <DialogHeader>
            <DialogTitle>Customize your booking username</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Pick a short, memorable handle. 3–40 characters, letters, numbers, hyphens.
              Old links keep working — anyone who visits your previous URL is auto-redirected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="slug-input" className="text-xs">Your URL</Label>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-500 shrink-0">{origin}/book/</span>
              <Input
                id="slug-input"
                value={candidate}
                onChange={(e) => onCandidateChange(e.target.value)}
                placeholder="your-name"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck="false"
                data-testid="slug-input"
              />
            </div>
            <div className="min-h-[20px] text-xs flex items-center gap-1">
              {checkState.checking && (
                <span className="text-gray-500 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> checking…
                </span>
              )}
              {!checkState.checking && checkState.available === true && (
                <span className="text-emerald-700 inline-flex items-center gap-1" data-testid="slug-available">
                  <Check className="w-3 h-3" /> Available — you'll become <code className="bg-emerald-50 px-1 rounded">/book/{checkState.normalized}</code>
                </span>
              )}
              {!checkState.checking && checkState.available === false && (
                <span className="text-red-700 inline-flex items-center gap-1" data-testid="slug-unavailable">
                  <X className="w-3 h-3" /> {checkState.reason || 'Not available'}
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={saveCustomSlug}
              disabled={saving || !checkState.available}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="save-slug-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save username
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MyBookingLinkCard;
